import { Fruit } from "../object/Fruit";
import styles from "./index.module.scss";
import classNames from "classnames/bind";
import { useState, useEffect } from "react";
import { db } from "../../../firebase";
import { collection, query, orderBy, limit, getDocs } from "firebase/firestore";

const cx = classNames.bind(styles);

interface IntroProps {
  isVisible: boolean;
  handleGameStart: () => void;
}

interface Ranking {
  name: string;
  score: number;
  createdAt: Date;
}

const Intro = ({ isVisible, handleGameStart }: IntroProps) => {
  const [showRankings, setShowRankings] = useState(false);
  const [showProduct, setShowProduct] = useState(false);
  const [rankings, setRankings] = useState<Ranking[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [toastVisible, setToastVisible] = useState(false);
  let timeout: NodeJS.Timeout | null = null;

  useEffect(() => {
    const updateScale = () => {
      const root = document.documentElement;
      const windowHeight = window.innerHeight;
      const windowWidth = window.innerWidth;
      const aspectRatio = windowWidth / windowHeight;

      // 세로가 더 긴 경우 (예: SE와 같은 세로형 화면)
      if (aspectRatio < 0.5) {
        const scale = Math.min(windowHeight / 800, windowWidth / 400);
        root.style.setProperty("--scale-ratio", scale.toString());
      } else {
        root.style.setProperty("--scale-ratio", "1");
      }
    };

    updateScale();
    window.addEventListener("resize", updateScale);
    return () => window.removeEventListener("resize", updateScale);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (showRankings) {
        setShowRankings(false);
      }
      if (showProduct) {
        setShowProduct(false);
      }
    };

    document.addEventListener("click", handleClickOutside);
    return () => {
      document.removeEventListener("click", handleClickOutside);
    };
  }, [showRankings, showProduct]);

  const positionCircularly = (totalElements: number, index: number) => {
    const radius = 150;
    const angle = (2 * Math.PI * index) / totalElements;

    const x = radius * Math.cos(angle);
    const y = radius * Math.sin(angle);

    return {
      top: `calc(50% + ${y}px - 24px)`,
      left: `calc(50% + ${x}px - 24px)`,
    };
  };

  const fruitItemEls = Object.keys(Fruit)
    .slice(0, Object.keys(Fruit).length - 1)
    .map((fruit, index) => {
      const itemPositions = positionCircularly(11, index);

      return (
        <li
          key={fruit}
          className={cx("listItem")}
          style={{
            backgroundImage: `url(/resource/${fruit}.png)`,
            top: itemPositions.top,
            left: itemPositions.left,
          }}
        />
      );
    });

  const handleRankingsClick = async (event: React.MouseEvent) => {
    event.stopPropagation(); // 이벤트 버블링 방지

    if (showRankings) {
      setShowRankings(false);
      return;
    }

    setIsLoading(true);
    try {
      const rankingsRef = collection(db, "rankings");
      const q = query(rankingsRef, orderBy("score", "desc"), limit(10));
      const querySnapshot = await getDocs(q);
      const rankingsData = querySnapshot.docs.map((doc) => ({
        ...doc.data(),
        createdAt: doc.data().createdAt.toDate(),
      })) as Ranking[];
      setRankings(rankingsData);
      setShowRankings(true);
    } catch (error) {
      console.error("랭킹을 불러오는 중 오류 발생:", error);
      alert("랭킹을 불러오는데 실패했습니다.");
    }
    setIsLoading(false);
  };


  const share = () => {
    if (navigator.share) {
      navigator
        .share({
          title: "공공장장의 순원볼 게임",
          text: "도자기들을 모아 막걸리잔을 만들어보세요.",
          url: "https://yido-rho.vercel.app/",
        })
        .then(() => console.log("done"))
        .catch((error) => console.log(error));
    } else {
      timeout && clearTimeout(timeout);
      const urlToCopy = window.location.href;

      if (document.queryCommandSupported("copy")) {
        const input = document.createElement("input");
        input.value = urlToCopy;
        document.body.appendChild(input);
        input.select();
        document.execCommand("copy");
        document.body.removeChild(input);
      } else {
        navigator.clipboard.writeText(urlToCopy);
      }

      setToastVisible(true);
      timeout = setTimeout(() => {
        setToastVisible(false);
      }, 2800);
    }
  };

  if (!isVisible) return null;

  return (
    <div className={cx("introArea")}>
      {/* 공유하기 버튼 */}
      {/* 상단 공유 버튼 제거 */}
      {/* 자사몰 바로가기 버튼 제거 (할인행사) */}
      {/* 토스트 메시지 */}
      <div className={cx("toastArea", { show: toastVisible })}>
        🍉URL이 복사되었습니다.
      </div>
      <div className={cx("introArea")}>
        {/* 돌아가는 아이템들 */}
        <ul className={cx("listWrap")}>{fruitItemEls}</ul>

        {/* 상단 이벤트 배너 */}
        <div className={cx("eventBannerTop")}>
          <div>POP-UP EVENT</div>
          <div>최종 달항아리 성공 시</div>
          <div>실제 달항아리를 보내드립니다!</div>
          <div>이벤트 기간 : ~ 10/2</div>
        </div>

        {/* 회전과 무관한 고정된 중앙 요소 */}
        <div className={cx("titleArea")}>
          <div className={cx("moonjarContainer")} onClick={handleGameStart}>
            <img
              src="/resource/moonjar.png"
              alt="달항아리"
              className={cx("moonjarImage")}
            />
            <div className={cx("gameStartText")}>GAME<br />START</div>
          </div>
        </div>

      </div>

      {/* 하단 액션 영역: 2x2 그리드 */}
      <div className={cx("bottomActions")}>
        <button className={cx("actionBtn")} onClick={handleRankingsClick}>
          {showRankings ? "랭킹 닫기" : "랭킹 확인"}
        </button>
        <button className={cx("actionBtn", "shareVariant")} onClick={share}>
          공유하기
        </button>
        <a
          href="https://www.instagram.com/yidopottery"
          target="_blank"
          rel="noopener noreferrer"
          className={cx("instagramBtn")}
        >
          ✨ 인스타 팔로우하면
        </a>
        <a
          href="https://www.instagram.com/gonggongjangjang"
          target="_blank"
          rel="noopener noreferrer"
          className={cx("instagramBtn")}
        >
          ✨ 운이 좋을지도..?
        </a>
      </div>
      {showRankings && (
        <div
          className={cx("rankingsArea")}
          onClick={(e) => e.stopPropagation()}
        >
          <h2 className={cx("rankingsTitle")}>🏆 TOP 10 🏆</h2>
          <p className={cx("rankingsDescription")}>
            랭킹은 10/2 23:59에 초기화됩니다.
          </p>
          {isLoading ? (
            <div className={cx("loading")}>로딩중...</div>
          ) : rankings.length > 0 ? (
            <ul className={cx("rankingsList")}>
              {rankings.map((ranking, index) => (
                <li key={index} className={cx("rankingItem")}>
                  <span className={cx("rank")}>{index + 1}</span>
                  <span className={cx("name")}>{ranking.name}</span>
                  <span className={cx("score")}>{ranking.score}</span>
                </li>
              ))}
            </ul>
          ) : (
            <div className={cx("noRankings")}>아직 랭킹이 없습니다.</div>
          )}
        </div>
      )}

      {/* 기존 상품(할인행사) 섹션은 제거 */}
    </div>
  );
};

export default Intro;
